import * as yup from 'yup'
import { emailYup } from './emailYup'
import pirTerminology from '../util/pirTerminology'

const phoneRegex = /^\(?([0-9]{3})\)? ([0-9]{3})[-]?([0-9]{4})/
const postalCodeRegex = /^[A-Z][0-9][A-Z] [0-9][A-Z][0-9]/

const propertySchema = yup.object().shape({
  address: yup.string().required('Address is required'),
  unit: yup.string(),
  city: yup.string().required('City is required'),
  province: yup.string().required('Province is required'),
  postalCode: yup
    .string()
    .matches(postalCodeRegex, { message: 'Postal Code must be valid', excludeEmptyString: true })
    .required('Postal Code is required')
})

const ownerSchema = yup.object().shape({
  ownedBy: yup.string().required('You must indicate who owns this property'),
  companyName: yup.string().when('ownedBy', {
    is: 'company',
    then: () => yup.string().required('Company Name is required')
  }),
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  address: yup.string().required('Address is required'),
  signatureSource: yup.string().required('You must indicate how you will provide authorization'),
  signatureFile: yup.mixed().when('signatureSource', {
    is: 'upload',
    then: () => yup.mixed().required('Authorization file is required')
  }),
  phone: yup.string().when('signatureSource', {
    is: 'electronic',
    then: () =>
      yup
        .string()
        .transform((p, o) => (o === '' ? null : p))
        .matches(phoneRegex, 'Phone must be valid')
        .required('Phone is required')
  }),
  email: yup.string().when('signatureSource', {
    is: 'electronic',
    then: () => emailYup().required('Email is required')
  }),
  confirmEmail: yup.string().when('signatureSource', {
    is: 'electronic',
    then: () =>
      yup
        .string()
        .required('Confirm email is required')
        .oneOf([yup.ref('email')], 'Emails must match')
  })
})

const strataSchema = yup.object().shape({
  role: yup.string().required('Role is required'),
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  address: yup.string().required('Address is required'),
  phone: yup
    .string()
    .transform((p, o) => (o === '' ? null : p))
    .matches(phoneRegex, 'Phone must be valid')
    .required('Phone is required'),
  email: emailYup().required('Email is required'),
  confirmEmail: yup
    .string()
    .required('Confirm email is required')
    .oneOf([yup.ref('email')], 'Emails must match'),
  additionalName: yup.string(),
  strataNumber: yup.string().required(() => `${pirTerminology.getAssociationTitle()} Number is required`)
})

const requestorSchema = yup.object().shape({
  isRealtor: yup.boolean().required('You must indicate if you are a realtor'),
  brokerageName: yup.string(),
  firstName: yup.string().required('First Name is required'),
  lastName: yup.string().required('Last Name is required'),
  address: yup.string().required('Address is required'),
  phone: yup
    .string()
    .transform((p, o) => (o === '' ? null : p))
    .matches(phoneRegex, 'Phone must be valid')
    .required('Phone is required'),
  email: emailYup().required('Email is required'),
  confirmEmail: yup
    .string()
    .required('Confirm email is required')
    .oneOf([yup.ref('email')], 'Emails must match'),
  additionalName: yup.string()
})

const pirSchema = yup.object().shape({
  strataState: yup.string(),
  propertyInformation: propertySchema,
  ownerInformation: ownerSchema,
  strataInformation: yup.object().when('strataState', {
    is: strataState => ['autoStrata', 'manualStrata'].includes(strataState),
    then: () => strataSchema,
    otherwise: () => yup.object().nullable().notRequired()
  }),
  requestorInformation: requestorSchema,
  requestInformation: yup
    .object()
    .shape({
      tier: yup.string().when('$tierPricingEnabled', {
        is: true,
        then: schema => schema.required('Please select a tier'),
        otherwise: schema => schema.notRequired()
      }),
      tierOptions: yup.array().when('$tierPricingEnabled', {
        is: true,
        then: schema => schema,
        otherwise: schema => schema.notRequired()
      }),
      additionalDetails: yup.string()
    })
    .nullable()
    .when('$tierPricingEnabled', {
      is: true,
      then: schema => schema.required('Please select a tier'),
      otherwise: schema => schema.notRequired()
    }),
  additionalRecipients: yup.array().of(
    yup.object().shape({
      email: emailYup().required('Email is required')
    })
  )
})

const pirEditSchema = yup.object().shape({
  strataState: yup.string().oneOf(['manualStrata', 'manualNoStrata']).required(),
  ownerInformation: ownerSchema.shape({
    signatureFile: yup.mixed().when('signatureSource', {
      is: 'upload',
      then: () =>
        yup.mixed().test('authorization-file-required', 'Authorization file is required', function (value) {
          if (value) return true
          const originalSource = (this.options.context?.originalOwnerSignatureSource || '').toLowerCase()
          if (originalSource !== 'upload') return false
          const firstNameChanged = this.parent.firstName !== this.options.context?.originalOwnerFirstName
          const lastNameChanged = this.parent.lastName !== this.options.context?.originalOwnerLastName
          return !firstNameChanged && !lastNameChanged
        })
    })
  }),
  strataInformation: yup.object().when('strataState', {
    is: 'manualStrata',
    then: () => strataSchema,
    otherwise: () => yup.object().nullable().notRequired()
  }),
  requestorInformation: requestorSchema.omit(['isRealtor', 'brokerageName', 'confirmEmail']),
  additionalRecipients: yup.array().of(
    yup.object().shape({
      email: emailYup().required('Email is required')
    })
  )
})

export { pirSchema, pirEditSchema, ownerSchema, strataSchema, requestorSchema }
